/**
 * Filter Analytics — admin dashboard for map filter-tap data.
 *
 * What this page is for
 * ─────────────────────
 * Every time a passenger or driver taps a category chip on the map
 * filter rail (e.g. "Eat & Drink", "Fuel", "ATM"), the mobile app
 * logs a row in `public.map_filter_events`. This page rolls that
 * data up so the admin can answer:
 *
 *   • What categories are passengers actually browsing for?
 *   • Which corridor has the highest demand for fuel?
 *   • What time of day are riders looking for restaurants?
 *
 * That intel directly fuels B2B sponsorship sales:
 *   "Restaurants got 18,400 taps last month on Roseau↔Portsmouth
 *    between 4–7 PM" → email to KFC / Pearl's Cuisine.
 *
 * Data source
 * ───────────
 * Single RPC: `public.admin_filter_analytics(p_days_back, p_audience)`
 * (see migration 20260620630000). Returns four rollups in one
 * round trip: summary, per_filter, per_corridor, per_hour.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  BarChart3,
  Users,
  Clock,
  MapPin,
  Activity,
  RefreshCw,
} from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────

type Audience = 'all' | 'passenger' | 'driver';

interface SummaryRollup {
  total_taps: number;
  unique_users: number;
  distinct_filters: number;
  distinct_corridors: number;
  oldest_event: string | null;
  newest_event: string | null;
}

interface PerFilterRow {
  filter_id: string;
  taps: number;
  unique_users: number;
  share_pct: number;
}

interface PerCorridorRow {
  corridor_id: string;
  corridor_name: string;
  taps: number;
  top_filter_id: string | null;
}

interface PerHourRow {
  local_hour: number;
  taps: number;
}

interface AnalyticsEnvelope {
  ok: boolean;
  error?: string;
  days_back: number;
  audience: Audience;
  summary: SummaryRollup;
  per_filter: PerFilterRow[];
  per_corridor: PerCorridorRow[];
  per_hour: PerHourRow[];
}

// ── Filter label / colour map ─────────────────────────────────────────────
// Mirrors the keys defined in mobile `src/services/monetization/mapFilters.ts`
// — keep in sync if you add new chips. Unknown keys fall through to a
// neutral grey so the table never crashes on a stale row.

const FILTER_META: Record<string, { label: string; color: string; emoji: string }> = {
  all: { label: 'All', color: '#10b981', emoji: '🟢' },
  eat: { label: 'Eat & Drink', color: '#ef4444', emoji: '🍽️' },
  shop: { label: 'Shop', color: '#ec4899', emoji: '🛍️' },
  fuel: { label: 'Fuel', color: '#f97316', emoji: '⛽' },
  money: { label: 'Money', color: '#1d4ed8', emoji: '💰' },
  care: { label: 'Care', color: '#14b8a6', emoji: '💊' },
  stay: { label: 'Stay', color: '#4f46e5', emoji: '🏨' },
  explore: { label: 'Explore', color: '#0ea5e9', emoji: '🧭' },
};

function metaFor(filterId: string) {
  return FILTER_META[filterId] ?? { label: filterId, color: '#6b7280', emoji: '·' };
}

function fmtHour(h: number): string {
  // 24-hour AST → friendly "4 AM", "1 PM", etc.
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h < 12) return `${h} AM`;
  return `${h - 12} PM`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ── Component ─────────────────────────────────────────────────────────────

export default function FilterAnalytics() {
  const [audience, setAudience] = useState<Audience>('all');
  const [daysBack, setDaysBack] = useState(30);
  const [data, setData] = useState<AnalyticsEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: rpcData, error: rpcErr } = await supabase.rpc(
        'admin_filter_analytics',
        { p_days_back: daysBack, p_audience: audience },
      );
      if (rpcErr) throw rpcErr;
      const envelope = rpcData as AnalyticsEnvelope | null;
      if (!envelope || envelope.ok === false) {
        throw new Error(envelope?.error || 'Forbidden: admin access required');
      }
      setData(envelope);
    } catch (err: any) {
      setError(err.message || 'Failed to load filter analytics');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysBack, audience]);

  // Pre-compute the y-axis max once so the per-hour bar chart and
  // per-filter bars share a consistent scale within their own
  // section. Done in useMemo so a re-render never recalculates these
  // unless the underlying data changed.
  const filterMax = useMemo(
    () =>
      data?.per_filter?.length
        ? Math.max(...data.per_filter.map((r) => r.taps))
        : 0,
    [data?.per_filter],
  );
  const hourMax = useMemo(
    () =>
      data?.per_hour?.length
        ? Math.max(...data.per_hour.map((r) => r.taps))
        : 0,
    [data?.per_hour],
  );
  const corridorMax = useMemo(
    () =>
      data?.per_corridor?.length
        ? Math.max(...data.per_corridor.map((r) => r.taps))
        : 0,
    [data?.per_corridor],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Filter Analytics</h1>
          <p className="text-sm text-gray-400 mt-1">
            Every time a rider or driver taps a category chip on the map filter rail.
            Use this to spot demand and pitch new sponsors.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Audience tabs — click cycles passenger ↔ driver ↔ all */}
          <div className="inline-flex rounded-md border border-gray-700 bg-gray-900 overflow-hidden">
            {(['all', 'passenger', 'driver'] as Audience[]).map((aud) => (
              <button
                key={aud}
                type="button"
                onClick={() => setAudience(aud)}
                className={`px-3 py-1.5 text-sm capitalize transition-colors ${
                  audience === aud
                    ? 'bg-cyan-500/20 text-cyan-300'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {aud}
              </button>
            ))}
          </div>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="bg-gray-900 border border-gray-700 text-white text-sm rounded-md px-3 py-1.5 focus:border-emerald-500"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-700 text-gray-300"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Loading / error / empty states */}
      {loading && !data && (
        <div className="bg-gray-900/40 border border-gray-800 rounded-xl flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading analytics…
        </div>
      )}

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
          <p className="text-sm text-rose-300">Failed to load: {error}</p>
          <p className="text-xs text-rose-200/70 mt-2">
            Make sure migration{' '}
            <code className="font-mono text-cyan-300">
              20260620630000_map_filter_analytics
            </code>{' '}
            has been applied to the database.
          </p>
        </div>
      )}

      {!loading && !error && data && data.summary.total_taps === 0 && (
        <div className="bg-gray-900/40 border border-gray-800 rounded-xl p-12 text-center">
          <Activity className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">
            No filter taps in this window yet.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            As soon as a passenger or driver taps a chip on the map filter rail in
            the mobile app, it will land here. The data starts flowing the moment
            the migration is applied. There's nothing else to wire up.
          </p>
        </div>
      )}

      {!loading && !error && data && data.summary.total_taps > 0 && (
        <>
          {/* Stat strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              label="Total taps"
              value={data.summary.total_taps.toLocaleString()}
              icon={<Activity className="w-4 h-4 text-cyan-400" />}
            />
            <StatCard
              label="Unique users"
              value={data.summary.unique_users.toLocaleString()}
              icon={<Users className="w-4 h-4 text-emerald-400" />}
            />
            <StatCard
              label="Categories used"
              value={`${data.summary.distinct_filters}`}
              icon={<BarChart3 className="w-4 h-4 text-amber-400" />}
            />
            <StatCard
              label="Corridors covered"
              value={`${data.summary.distinct_corridors}`}
              icon={<MapPin className="w-4 h-4 text-rose-400" />}
            />
          </div>

          {/* Window note */}
          <div className="text-xs text-gray-500 -mt-2">
            Showing taps from{' '}
            <span className="text-gray-300">{fmtDate(data.summary.oldest_event)}</span>
            {' '}through{' '}
            <span className="text-gray-300">{fmtDate(data.summary.newest_event)}</span>.
          </div>

          {/* Per-filter — the headline rollup. */}
          <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-white">
                  Tap demand by category
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  What people on the {audience === 'all' ? 'app' : `${audience} side`} were looking for.
                </p>
              </div>
            </header>
            <div className="space-y-2.5">
              {data.per_filter.map((row) => {
                const meta = metaFor(row.filter_id);
                const widthPct =
                  filterMax > 0 ? Math.max(2, (row.taps / filterMax) * 100) : 0;
                return (
                  <div key={row.filter_id} className="flex items-center gap-3">
                    <div className="w-32 shrink-0 flex items-center gap-2 text-sm text-gray-200">
                      <span aria-hidden>{meta.emoji}</span>
                      <span>{meta.label}</span>
                    </div>
                    <div className="flex-1 relative h-7 bg-gray-800/50 rounded-md overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 transition-all"
                        style={{
                          width: `${widthPct}%`,
                          backgroundColor: meta.color,
                          opacity: 0.85,
                        }}
                      />
                      <div className="absolute inset-0 flex items-center px-2.5">
                        <span className="text-xs font-semibold text-white drop-shadow">
                          {row.taps.toLocaleString()}
                        </span>
                        <span className="ml-auto text-[11px] text-gray-200/80">
                          {row.share_pct}% · {row.unique_users} {row.unique_users === 1 ? 'user' : 'users'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Per-hour — distribution by hour of day in Dominica AST. */}
          <section className="bg-gray-900/40 border border-gray-800 rounded-xl p-5">
            <header className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-400" />
                  Tap activity by hour
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  All times in Dominica local time (AST, UTC-4).
                </p>
              </div>
            </header>
            <div className="grid grid-cols-12 sm:grid-cols-24 gap-1 items-end h-32">
              {data.per_hour.map((row) => {
                const heightPct =
                  hourMax > 0 ? Math.max(4, (row.taps / hourMax) * 100) : 4;
                return (
                  <div
                    key={row.local_hour}
                    className="flex flex-col items-center gap-1 group cursor-help"
                    title={`${fmtHour(row.local_hour)} · ${row.taps.toLocaleString()} taps`}
                  >
                    <div className="flex-1 w-full flex items-end">
                      <div
                        className="w-full rounded-sm bg-gradient-to-t from-cyan-600 to-cyan-400 group-hover:from-cyan-500 group-hover:to-cyan-300 transition-all"
                        style={{ height: `${heightPct}%` }}
                      />
                    </div>
                    <div className="text-[9px] text-gray-500 font-mono">
                      {row.local_hour}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-gray-500 px-1">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>Noon</span>
              <span>6 PM</span>
              <span>11 PM</span>
            </div>
          </section>

          {/* Per-corridor — where demand is concentrated. */}
          <section className="bg-gray-900/40 border border-gray-800 rounded-xl overflow-hidden">
            <header className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  Tap demand by corridor
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  What riders on each route are hunting for. Use this for
                  corridor-specific sponsor pitches.
                </p>
              </div>
            </header>
            {data.per_corridor.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                No corridor-tagged taps in this window.
              </div>
            ) : (
              <div className="divide-y divide-gray-800/60">
                {data.per_corridor.map((row) => {
                  const meta = row.top_filter_id ? metaFor(row.top_filter_id) : null;
                  const widthPct =
                    corridorMax > 0
                      ? Math.max(4, (row.taps / corridorMax) * 100)
                      : 0;
                  return (
                    <div
                      key={row.corridor_id}
                      className="px-5 py-3 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">
                            {row.corridor_name}
                          </div>
                          <div className="mt-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                              style={{ width: `${widthPct}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-white">
                            {row.taps.toLocaleString()}
                          </div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                            taps
                          </div>
                        </div>
                        {meta && (
                          <div
                            className="px-2.5 py-1 rounded-full text-[11px] font-medium flex items-center gap-1.5 shrink-0"
                            style={{
                              backgroundColor: `${meta.color}22`,
                              color: meta.color,
                              border: `1px solid ${meta.color}55`,
                            }}
                          >
                            <span aria-hidden>{meta.emoji}</span>
                            <span>Top: {meta.label}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Footer note for sales staff. */}
          <div className="text-[11px] text-gray-500 leading-relaxed">
            Tip: pair the top-corridor breakdown with the time-of-day chart when
            pitching a sponsor. <em>"Eat &amp; Drink peaks at 6 PM on
            Roseau↔Portsmouth"</em> is a tighter story than
            <em> "people like restaurants"</em>.
          </div>
        </>
      )}
    </div>
  );
}

// ── Local components ──────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1.5">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}
